import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bell,
  CheckCircle2,
  KeyRound,
  Shield,
  UserRound,
} from "lucide-react";
import type { AuditEventRecord } from "@packages/global";
import { PageHeader } from "@packages/ui";
import { useAuthStore } from "../../stores/authStore";
import { accountActivityApi } from "../../services/user";
import { getAuditActionLabel, toNotification } from "../../utils/auditNotifications";

type Tone = "teal" | "amber" | "blue" | "red" | "green" | "neutral";

const toneClass: Record<Tone, { surface: string; text: string; border: string }> = {
  teal: {
    surface: "bg-primary-soft",
    text: "text-primary-ink",
    border: "border-action-primary-border",
  },
  amber: {
    surface: "bg-warning-soft",
    text: "text-warning",
    border: "border-[#F1DDBD]",
  },
  blue: {
    surface: "bg-[#E1ECF8]",
    text: "text-[#2F73C7]",
    border: "border-[#CADDF2]",
  },
  red: {
    surface: "bg-danger-soft",
    text: "text-[#D13A32]",
    border: "border-[#EFC7C4]",
  },
  green: {
    surface: "bg-success-soft",
    text: "text-success",
    border: "border-[#CCE4D1]",
  },
  neutral: {
    surface: "bg-bg-elevated",
    text: "text-text-muted",
    border: "border-border-subtle",
  },
};

const DashboardPage: React.FC = () => {
  const { i18n } = useTranslation(["app", "nav"]);
  const isZh = i18n.language?.toLowerCase().startsWith("zh");
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [notificationRows, setNotificationRows] = useState<AuditEventRecord[]>([]);
  const [recentActivityRows, setRecentActivityRows] = useState<AuditEventRecord[]>([]);
  const [notificationReadIds, setNotificationReadIds] = useState<string[]>([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [attentionCount, setAttentionCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [notificationsResult, recentActivityResult, attentionItems] = await Promise.all([
          accountActivityApi.notifications({ page: 1, pageSize: 5 }),
          accountActivityApi.page({ page: 1, pageSize: 5 }),
          accountActivityApi.page({ page: 1, pageSize: 1, attentionOnly: true }),
        ]);
        setNotificationRows(notificationsResult.items);
        setRecentActivityRows(recentActivityResult.items);
        setNotificationReadIds(notificationsResult.readEventIds);
        setNotificationUnreadCount(notificationsResult.unreadCount);
        setAttentionCount(attentionItems.total);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const notifications = useMemo(
    () => notificationRows.map(toNotification).slice(0, 4),
    [notificationRows],
  );

  const stats = [
    {
      key: "profile",
      title: isZh ? "账号" : "Account",
      value: user?.name || user?.email || "-",
      hint: user?.email || (isZh ? "个人资料" : "Profile"),
      icon: UserRound,
      tone: "teal" as Tone,
    },
    {
      key: "role",
      title: isZh ? "角色" : "Role",
      value: user?.role || "-",
      hint: isZh ? "当前登录身份" : "Current session",
      icon: Shield,
      tone: "blue" as Tone,
    },
    {
      key: "todo",
      title: isZh ? "待关注" : "Attention",
      value: loading ? "-" : String(attentionCount),
      hint: isZh ? "失败或高风险事件" : "Risk or failed events",
      icon: AlertTriangle,
      tone: "amber" as Tone,
    },
    {
      key: "unread",
      title: isZh ? "未读通知" : "Unread",
      value: loading ? "-" : String(notificationUnreadCount),
      hint: isZh ? "来自平台通知流" : "Platform notifications",
      icon: Bell,
      tone: notificationUnreadCount > 0 ? "red" as Tone : "green" as Tone,
    },
  ];

  const handleOpenNotification = async (item: ReturnType<typeof toNotification>) => {
    const wasUnread = !notificationReadIds.includes(item.id);
    if (wasUnread) {
      setNotificationReadIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
      setNotificationUnreadCount((prev) => Math.max(0, prev - 1));
      accountActivityApi.markNotificationRead(item.id).catch(() => {
        setNotificationReadIds((prev) => prev.filter((id) => id !== item.id));
        setNotificationUnreadCount((prev) => prev + 1);
      });
    }
    if (item.targetPath) {
      navigate(item.targetPath);
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        eyebrow="My workspace"
        title={isZh ? "个人中心" : "Personal Center"}
        description={
          isZh
            ? "查看账号状态、平台通知与最近活动。"
            : "Review account status, platform notifications, and recent activity."
        }
      />

      <div className="-mx-4 space-y-[22px] bg-bg-page px-4 sm:-mx-6 sm:px-6">
        <section className="grid gap-[14px] md:grid-cols-2 xl:grid-cols-4">
          {stats.map(({ key, ...stat }) => (
            <StatCard key={key} {...stat} />
          ))}
        </section>

        <section className="grid items-stretch gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(340px,1fr)]">
          <article className="h-full overflow-hidden rounded-[12px] border border-border-subtle bg-bg-surface">
            <div className="flex items-center justify-between border-b border-border-subtle px-4 py-[13px]">
              <h2 className="text-[14px] font-semibold text-text-primary">{isZh ? "最近活动" : "Recent activity"}</h2>
              <button
                type="button"
                onClick={() => navigate("/recent")}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-primary-ink hover:text-primary-ink"
              >
                {isZh ? "查看全部" : "View all"}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
            {loading ? (
              <div className="px-4 py-10 text-[13px] text-text-muted">{isZh ? "加载中..." : "Loading..."}</div>
            ) : recentActivityRows.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Activity className="mx-auto h-8 w-8 text-text-muted" />
                <p className="mt-3 text-[13px] text-text-secondary">{isZh ? "暂无活动记录" : "No activity yet"}</p>
              </div>
            ) : (
              <ul>
                {recentActivityRows.slice(0, 5).map((item) => (
                  <li key={item.id} className="flex items-center gap-3 border-b border-border-subtle px-4 py-[13px] last:border-b-0">
                    <ActivityIcon item={item} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold text-text-primary">
                        {getAuditActionLabel(item.action)}
                      </span>
                      <span className="mt-1 block truncate text-[12.5px] text-text-secondary">
                        {item.targetName || item.targetId || item.module}
                      </span>
                    </span>
                    <span className="shrink-0 text-[11.5px] text-text-muted">
                      {relativeTime(item.occurredAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </article>

          <aside className="flex h-full flex-col gap-5">
            <article className="overflow-hidden rounded-[12px] border border-border-subtle bg-bg-surface">
              <div className="flex items-center justify-between border-b border-border-subtle px-4 py-[13px]">
                <h2 className="text-[14px] font-semibold text-text-primary">{isZh ? "通知提醒" : "Notifications"}</h2>
                <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-danger px-2 text-[12px] font-semibold text-white">
                  {notificationUnreadCount || notifications.length}
                </span>
              </div>
              {loading ? (
                <div className="px-4 py-8 text-[13px] text-text-muted">{isZh ? "加载中..." : "Loading..."}</div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-[13px] text-text-muted">{isZh ? "暂无通知" : "No notifications"}</div>
              ) : (
                <ul>
                  {notifications.map((item) => (
                    <li key={item.id} className="border-b border-border-subtle px-4 py-[13px] last:border-b-0">
                      <button type="button" onClick={() => void handleOpenNotification(item)} className="flex w-full gap-3 text-left">
                        <NotificationIcon item={item} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13.5px] font-semibold text-text-primary">{item.title}</span>
                          <span className="mt-1 block text-[12.5px] leading-5 text-text-secondary">{item.description}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </article>

            <article className="rounded-[12px] border border-border-subtle bg-bg-surface p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-primary-soft text-primary-ink">
                  <KeyRound className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-[14px] font-semibold text-text-primary">{isZh ? "账号安全" : "Account security"}</h2>
                  <p className="mt-1 text-[12.5px] text-text-secondary">{isZh ? "定期更新密码并维护联系方式。" : "Keep your password and contact details current."}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/change-password")}
                  className="inline-flex min-h-8 items-center rounded-[8px] bg-action-primary px-3 text-[12px] font-semibold text-white hover:bg-action-primary-hover"
                >
                  {isZh ? "改密" : "Update"}
                </button>
              </div>
            </article>
          </aside>
        </section>
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  title: string;
  value: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: Tone;
}> = ({ title, value, hint, icon: Icon, tone }) => (
  <article className="rounded-[12px] border border-border-subtle bg-bg-surface p-4">
    <div className="flex items-center gap-[13px]">
      <div className={`flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[12px] ${toneClass[tone].surface} ${toneClass[tone].text}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-[12px] font-medium text-text-secondary">{title}</div>
        <div className="mt-[3px] truncate text-[24px] font-semibold leading-none text-text-primary">{value}</div>
        <div className="mt-[3px] truncate text-[10.5px] text-text-muted">{hint}</div>
      </div>
    </div>
  </article>
);

const NotificationIcon: React.FC<{ item: ReturnType<typeof toNotification> }> = ({ item }) => {
  const tone: Tone = item.badge === "失败" || item.badge === "需关注" ? "red" : item.badge === "系统" ? "blue" : "amber";
  const Icon = item.badge === "失败" || item.badge === "需关注" ? AlertTriangle : item.badge === "系统" ? Bell : CheckCircle2;
  return (
    <span className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] ${toneClass[tone].surface} ${toneClass[tone].text}`}>
      <Icon className="h-4 w-4" />
    </span>
  );
};

const ActivityIcon: React.FC<{ item: AuditEventRecord }> = ({ item }) => {
  const Icon = item.module === "auth" ? UserRound : item.module === "admin" ? Shield : Activity;
  const tone: Tone = item.result === "success" ? "neutral" : "red";
  return <Icon className={`h-4.5 w-4.5 shrink-0 ${toneClass[tone].text}`} />;
};

function relativeTime(value: string): string {
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  if (Number.isNaN(diff)) return "";
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  return date.toLocaleDateString();
}

export default DashboardPage;
